import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import compose from 'lodash.flowright';
import { mapProps } from 'recompose';
import { withRouter, matchPath } from 'react-router-dom';
import { connect } from 'react-redux';
import { useMediaQuery } from 'react-responsive';

import getAlbumMembershipGroupIdentity from '../../../../../../../../temp/views/memberships/AlbumMembershipGroup/graphql/queries/getAlbumMembershipGroupIdentity';
import { createLoadMoreMethod } from '../../../../../../../../../util/apollo';
import DismissButton from '../../../../../../../../../components/global/DismissButton';
import PhotoGrid from '../../../../../../../../../components/grid/PhotoGrid';
import { isPhotoEntitledById } from '../../../../helpers';
import MatchedPhoto from '../../../MatchedPhoto';
import ShoppingBasketIcon from '../../../../../../../../../components/icons/ShoppingBasketIcon';
import Button from '../../../../../../../../../components/global/Button';
import Badge from '../../../../../../../../../components/global/Badge';
import { PrintProductConfiguratorModal } from '../../../../../../../../../services/prints/components/PrintProductConfigurator';
import { getPhotoOrientation } from '../PrintProductsGroup/helpers';
import { getPrintSizeServiceIds } from '../../../../../../../../../util/print-product-configuration';
import Spinner from '../../../../../../../../../components/global/Spinner';
import AccountCardAlbumCustomPrintConfiguratorModal from '../AccountCardAlbumCustomPrintOption/AccountCardAlbumCustomPrintConfigurator/modal';
import { CUSTOM_PRINT_TYPE } from '../../../../../../../../../constants/pro-custom-prints';

const propTypes = {
  className: PropTypes.string,
  albumFolderTag: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  }).isRequired,
  identity: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }),
  albumMembershipGroup: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }),
  albumMembershipGroupIdentity: PropTypes.shape({
    matched_photos_extended: PropTypes.shape({
      total: PropTypes.number.isRequired,
      records: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string.isRequired,
        }).isRequired,
      ),
    }).isRequired,
  }),
  loadMore: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onPhotoSelect: PropTypes.func.isRequired,
  photoIsOptional: PropTypes.bool,
  overriddenPrintPackageItem: PropTypes.shape({
    id: PropTypes.string,
    print_product_configuration: PropTypes.shape({
      print_product: PropTypes.shape({
        id: PropTypes.string,
      }),
      print_product_size: PropTypes.shape({
        id: PropTypes.string,
      }),
    }),
  }),
  loading: PropTypes.bool,
};

const AlbumMembershipGroupTaggedPhotos = ({
  className,
  identity,
  albumMembershipGroup,
  albumMembershipGroupIdentity,
  albumFolderTag,
  loadMore,
  onClose,
  onPhotoSelect,
  photoIsOptional,
  overriddenPrintPackageItem,
  loading,
  watermarkSettings,
}) => {
  const classes = classNames({
    'modal-contained-wrap': true,
    [className]: !!className,
  });
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const isMobile = useMediaQuery({ maxWidth: 767 });

  const matchedPhotos =
    albumMembershipGroupIdentity?.matched_photos_extended ?? [];

  const printProductConfig =
    overriddenPrintPackageItem?.print_product_configuration;

  const isAccountCardCustomPrint =
    overriddenPrintPackageItem?.custom_print?.type ===
    CUSTOM_PRINT_TYPE.PRINT_PRODUCT;

  const TagLabel = ({ className: tagClassName }) => (
    <Badge
      badgeStyle="purple"
      className={classNames({
        'd-inline-flex purchasing-option-item-info-extra-tag': true,
        [tagClassName]: !!tagClassName,
      })}
    >
      {albumFolderTag.name}
    </Badge>
  );

  return (
    <div className={classes}>
      <div className="purchasing-options-list-header modal-contained-header">
        <span className="modal-contained-title">
          Select <TagLabel />
        </span>
        <DismissButton className="ml-auto" onClick={onClose} />
      </div>

      {loading && (
        <div className="d-flex">
          <Spinner className="m-auto" />
        </div>
      )}

      {!loading && matchedPhotos?.records?.length === 0 && (
        <p className="p-5 text-center">
          Sorry, it looks like we found no <TagLabel className="mx-1" /> photos
          in which you appear. You may want to select a different package
          {photoIsOptional ? (
            <>
              {' '}
              or{' '}
              <Button btnStyle="link" onClick={onClose}>
                continue without a <TagLabel className="mx-1" /> photo.
              </Button>{' '}
            </>
          ) : (
            '.'
          )}
        </p>
      )}

      <div className="modal-contained-content memberships-tagged-photos-grid">
        <PhotoGrid
          records={matchedPhotos.records ?? []}
          loadMore={loadMore}
          total={matchedPhotos.total}
          targetRowHeight={250}
          recordPhotoAccessor="photo"
          justifiedLayoutOptions={{
            targetRowHeightTolerance: isMobile ? 0.5 : 0.175,
            widowLayoutStyle: isMobile ? 'justify' : 'left',
          }}
          cellRenderer={({ record: matchedPhoto }) => {
            const isEntitled = isPhotoEntitledById(
              albumMembershipGroupIdentity,
              matchedPhoto.photo.id,
            );

            const purchaseButtonClasses = classNames({
              'matched-grid-photo-action-btn matched-grid-photo-action-btn-purchase': true,
              'with-icon': photoIsOptional,
            });

            return (
              <MatchedPhoto
                key={matchedPhoto.id}
                matchedPhoto={matchedPhoto}
                isEntitled={isEntitled}
                includeTagName={false}
                actions={
                  <Button
                    onClick={() => {
                      if (printProductConfig || isAccountCardCustomPrint) {
                        setSelectedPhoto(matchedPhoto.photo);
                      } else {
                        onPhotoSelect({ photo: matchedPhoto.photo });
                      }
                    }}
                    className={purchaseButtonClasses}
                  >
                    {photoIsOptional ? (
                      <Fragment>
                        <ShoppingBasketIcon size={12} className="mr-1" /> Select
                      </Fragment>
                    ) : (
                      'Select'
                    )}
                  </Button>
                }
              />
            );
          }}
        />
      </div>
      {printProductConfig && selectedPhoto && (
        <PrintProductConfiguratorModal
          forceOpen
          photo={selectedPhoto}
          photoId={selectedPhoto.id}
          printProductId={printProductConfig.print_product.id}
          printProductSizeId={printProductConfig.print_product_size.id}
          printProductSizeServiceIds={getPrintSizeServiceIds(
            printProductConfig,
          )}
          orientation={getPhotoOrientation(selectedPhoto)}
          onClose={onClose}
          buttonText="Continue"
          controlsDisabled
          onComplete={({ accountCard }) =>
            onPhotoSelect({ photo: selectedPhoto, accountCard })
          }
          watermarkSettings={watermarkSettings}
        />
      )}
      {isAccountCardCustomPrint && selectedPhoto && (
        <AccountCardAlbumCustomPrintConfiguratorModal
          forceOpen
          deleteAccountCardOnDismiss
          albumCustomPrint={{
            custom_print: overriddenPrintPackageItem?.custom_print,
          }}
          onFinished={({ accountCard }) => {
            onPhotoSelect({ photo: selectedPhoto, accountCard });
          }}
          buttonText="Done"
          identity={identity}
          albumMembershipGroup={albumMembershipGroup}
          photo={selectedPhoto}
        />
      )}
    </div>
  );
};

AlbumMembershipGroupTaggedPhotos.propTypes = propTypes;

const mapStateToProps = ({ checkout }) => ({
  activeIdentity: checkout.activeIdentity,
});

export { AlbumMembershipGroupTaggedPhotos as AlbumMembershipGroupTaggedPhotosComponent };

export default compose(
  withRouter,
  connect(mapStateToProps, null),
  mapProps(({ activeIdentity, albumFolderTag, ...props }) => {
    const match = matchPath(props.location.pathname, {
      path: `/app/${props.match.params.basename}/hierarchy-items/:hierarchyItemId([0-9a-zA-Z]{10,})`,
      exact: false,
    });

    const consumerMatch = matchPath(props.location.pathname, {
      path: `/consumer/matches/:albumMembershipGroupId([0-9a-zA-Z]{10,})`,
      exact: false,
    });

    return {
      ...props,
      albumMembershipGroupId:
        props.albumMembershipGroupId ??
        match?.params?.hierarchyItemId ??
        consumerMatch?.params?.albumMembershipGroupId,
      identityId: activeIdentity?.id,
      albumFolderTag,
      albumMembershipGroupFilters: [albumFolderTag.id],
    };
  }),
  getAlbumMembershipGroupIdentity,
  mapProps(({ data, ...props }) => ({
    ...props,
    albumMembershipGroupIdentity: data?.albumMembershipGroup?.identities?.[0],
    loading: data?.loading,
    loadMore: createLoadMoreMethod({
      source: data?.albumMembershipGroup,
      recordsPath: 'identities[0].matched_photos_extended',
      fetchMore: data?.fetchMore,
    }),
  })),
)(AlbumMembershipGroupTaggedPhotos);
